import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { setToken, setClinic, isAuthenticated } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = '/logo-holons.svg';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false); // Unchecked by default
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/dashboard');
    }
    // Show session expired message if redirected from 401
    if (localStorage.getItem('session_expired')) {
      localStorage.removeItem('session_expired');
      toast.info('Your session has expired — please log in again');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log('Attempting login to:', `${BACKEND_URL}/api/auth/login`);
      
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email, password, remember_me: rememberMe }),
      });

      console.log('Response status:', response.status);
      // Clone response to avoid "body disturbed" error with tracking scripts
      const responseClone = response.clone();
      let data;
      try {
        data = await response.json();
      } catch {
        data = await responseClone.json();
      }
      console.log('Response data:', data);

      if (response.ok) {
        setToken(data.token);
        setClinic(data.clinic);
        toast.success('Welcome back!');
        navigate('/dashboard');
      } else {
        toast.error(data.detail || 'Invalid email or password');
      }
    } catch (error) {
      console.error('Login error details:', error);
      toast.error(`Error: ${error.message || 'Network error. Please check your connection.'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left Side - Branding */}
      <div className="hidden md:flex relative items-center justify-center overflow-hidden bg-gradient-to-br from-[hsl(184_45%_88%)] via-[hsl(190_60%_90%)] to-[hsl(200_50%_92%)]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(56,189,248,0.1),transparent_50%),radial-gradient(circle_at_bottom_left,rgba(14,165,233,0.1),transparent_50%)]" />
        <div className="relative text-center px-8 z-10">
          <div className="flex justify-center mb-8">
              <img 
                src="/logo-holons.svg" 
                alt="HOLONS Logo" 
                className="w-[393px] h-auto"
              />
          </div>
          <div className="inline-block">
            <h2 className="text-2xl font-light tracking-[0.2em] text-gray-700 uppercase mb-2" style={{fontFamily: 'Chivo, sans-serif'}}>Veterinary</h2>
            <div className="h-px bg-gradient-to-r from-transparent via-primary to-transparent mb-2"></div>
            <p className="text-lg font-medium text-primary tracking-wider" style={{fontFamily: 'Karla, sans-serif'}}>CLIENT PORTAL</p>
          </div>
        </div>
      </div>
      
      {/* Right Side - Login Form */}
      <div className="flex items-start md:items-center justify-center p-4 pt-2 md:p-6 bg-background">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="space-y-4">
            {/* Mobile Logo */}
            <div className="flex md:hidden justify-center mb-1">
              <img 
                src="/logo-holons.svg" 
                alt="HOLONS Logo" 
                className="w-56 h-auto"
              />
            </div>
            <div className="md:hidden text-center mb-4">
              <h2 className="text-lg font-light tracking-wider text-gray-600 uppercase" style={{fontFamily: 'Chivo, sans-serif'}}>Veterinary</h2>
              <div className="h-px bg-gradient-to-r from-transparent via-primary to-transparent my-2 mx-auto w-32"></div>
              <p className="text-sm font-medium text-primary tracking-wider" style={{fontFamily: 'Karla, sans-serif'}}>CLIENT PORTAL</p>
            </div>
            <CardTitle className="text-2xl font-semibold">Sign in</CardTitle>
            <CardDescription>Access your clinic dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="clinic@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="remember-me" 
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                    data-testid="remember-me-checkbox"
                  />
                  <Label htmlFor="remember-me" className="text-sm cursor-pointer">
                    Remember me for 1 year
                  </Label>
                </div>
                <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                  Forgot password?
                </Link>
              </div>
              
              <Button 
                type="submit" 
                className="w-full mt-2" 
                disabled={loading}
                data-testid="login-form-submit-button"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>
            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">Don't have an account? </span>
              <Link to="/register" className="text-primary hover:underline font-medium">
                Register here
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
